import { createContext, useEffect, useRef, useState } from 'react'
import io from 'socket.io-client'
import { logger } from '../helpers/util'
import { useAuth } from '../hooks/auth.hook'
import { dispatchCustomEvent, Notification, NotificationContent, Text, usePubsub, useToast } from '@fold-dev/core'
import { useAppStore } from '../store/app'
import { useNotifications, useProjects, useProjectUnreads, useThreads } from '../hooks/api.hooks'
import { useParams } from 'react-router'
import { NOTIFICATION_ACTION } from '../constants'
import { FRONTEER_HEARTBEATS } from '../App'
import { EventName } from '../../../api/src/shared'
import * as Y from 'yjs'

const defaultWebsocketContext = {
    connected: false,
    disconnected: null,
    error: null,
    publish: (eventName: string, payload: any) => null,
}

export const WebsocketContext = createContext(defaultWebsocketContext)

export const emit = (roomId: string, action: string, payload: any) => {
    WebsocketService.getInstance().emit('sync', {
        roomId,
        action,
        payload,
    })
}

export const heartbeat = (roomId: string, payload: any) => {
    WebsocketService.getInstance().emit('heartbeat', {
        roomId,
        payload,
    })
}

export const typing = (roomId: string, payload: any) => {
    WebsocketService.getInstance().emit('typing', {
        roomId,
        payload,
    })
}

export const WebsocketProvider = (props) => {
    const [state, setState] = useState<any>(defaultWebsocketContext)
    const { workspaceId, projectId } = useParams()
    const { getToken, getUserId } = useAuth()
    const projectUnreadsAPI = useProjectUnreads()
    const projectsAPI = useProjects()
    const threadsAPI = useThreads()
    const notificationsAPI = useNotifications()
    const { showToast } = useToast()
    const disconnected = useRef(false)

    const [text, setText] = useState('')
    const ytextRef = useRef(null)
    const ydocRef = useRef(null)

    const handleConnect = () => {
        const userId = getUserId()
        WebsocketService.getInstance().emit('join', { token: getToken(), room: userId })
        logger('WS CONTEXT: CONNECTED: JOIN', userId)
        setState({
            ...state,
            connected: true,
            disconnected: false,
            error: null,
            publish: (eventName: string, payload: any) => WebsocketService.getInstance().emit(eventName, payload),
        })
        if (disconnected.current) {
            logger('WS CONTEXT: RELOAD')
            dispatchCustomEvent('reload')
            disconnected.current = false
        }
    }

    const handleDisconnect = (error: string) => {
        disconnected.current = true
        logger('WS CONTEXT: DISCONNECTED')
        setState({ ...state, connected: false, disconnected: error, error: null })
    }

    const handleError = (error: any) => {
        logger('WS CONTEXT: ERROR')
        setState({ ...state, connected: false, disconnected: false, error })
    }

    const handleEvent = (eventName: string, eventPayload: any) => {
        logger(eventName, eventPayload)
        const { actions, sidebar, thread, task, projects } = useAppStore.getState()

        switch (eventName) {
            case EventName.heartbeat:
                // eventPayload = userId
                window[FRONTEER_HEARTBEATS][eventPayload] = new Date().getTime()
                break

            case EventName.typing:
                // eventPayload = { id, name, time }
                dispatchCustomEvent('typing', eventPayload)
                break

            case EventName.unreadAdd:
                // eventPayload = projectId
                if (eventPayload == projectId) {
                    projectUnreadsAPI.remove(workspaceId, eventPayload)
                } else {
                    actions.unreadsAdd({ projectId: eventPayload })
                }
                break

            case EventName.threadUpdateAsUnread:
                // eventPayload = threadId
                if (eventPayload == thread.threadId) {
                    threadsAPI.update(workspaceId, eventPayload, { unread: false })
                    actions.threadsUpdate({ id: eventPayload, unread: false }, false)
                } else {
                    actions.threadsUpdate({ id: eventPayload, unread: true }, false)
                }
                break

            case EventName.reloadSubtasks:
                // eventPayload = taskId
                dispatchCustomEvent('reload:subtasks')
                break

            case EventName.newConversation:
                // eventPayload = project
                if (!projects.find((project) => project.id == eventPayload.id)) actions.projectAdd(eventPayload, false)
                break

            case EventName.teamUserAdd:
                // eventPayload = teamId
                dispatchCustomEvent('nav:refresh')
                break

            case EventName.teamUserRemove:
                // eventPayload = teamId
                actions.teamDelete(eventPayload, false)
                break

            case EventName.projectUserAdd:
                // eventPayload = projectId
                projectsAPI.getOne(workspaceId, eventPayload).then((project) => actions.projectAdd(project, false))
                break

            case EventName.projectUserRemove:
                // eventPayload = projectId
                actions.projectDelete(eventPayload, false)
                actions.starredDelete(eventPayload, false)
                break

            case EventName.projectUsersAdd:
                // Sent from the API!
                // eventPayload = { projectUser, projectId }
                dispatchCustomEvent('project:refresh-preview', { id: eventPayload.projectId })
                actions.projectUsersAdd(eventPayload, false)
                break

            case EventName.projectUsersRemove:
                // Sent from the API!
                // eventPayload = { projectUser, projectId }
                dispatchCustomEvent('project:refresh-preview', { id: eventPayload.projectId })
                actions.projectUsersDelete(eventPayload, false)
                break

            case EventName.notificationAdd:
                // eventPayload = notificationId
                notificationsAPI.getOne(workspaceId, eventPayload).then((notification) => {
                    // if the user is in the message modal
                    // and project is the same as the notification project
                    // and it's a message-mention notification
                    // then don't show the user, just delete it again
                    if (
                        notification.action == NOTIFICATION_ACTION.PROJECT.MESSAGE_MENTION &&
                        projectId == notification.projectId &&
                        sidebar == 'messages'
                    ) {
                        notificationsAPI.remove(workspaceId, notification.id)
                    } else {
                        actions.notificationAdd(notification)

                        // if the user has been mentioned, then notify them via toast
                        if (notification.action == NOTIFICATION_ACTION.PROJECT.MESSAGE_MENTION) {
                            showToast({
                                message: `You were just mentioned in ${notification.projectName}`,
                                anchor: 'top-right',
                            })
                        }
                    }
                })
                break

            case EventName.taskCommentAdd:
                // eventPayload = { taskComment }
                actions.commentAdd(eventPayload, false)
                break

            case EventName.taskAdd:
                // eventPayload = { task }
                actions.taskAdd(eventPayload, false)
                break

            case EventName.workspaceUpdated:
                // eventPayload = { ...workspace }
                actions.workspaceUpdate(eventPayload, false)
                break

            case EventName.updateProjectTotals:
                // eventPayload = { projectId, workspaceId, totalTaskCount, completeTaskCount, timestamp }
                actions.projectUpdate({ ...eventPayload, id: eventPayload.projectId }, false)
                break

            default:
                if (actions[eventName]) actions[eventName](eventPayload, false)
                break
        }

        /*        // applies the update to this ydoc
        if (eventName == 'y-update') {
            console.log(eventPayload, ydocRef.current, eventPayload instanceof Uint8Array)
            Y.applyUpdate(ydocRef.current, new Uint8Array(eventPayload))
        } */
    }

    /* 
    useEffect(() => {
        const ydoc = new Y.Doc()
        ydocRef.current = ydoc
        const ytext = ydoc.getText('shared-text')
        ytextRef.current = ytext

        // if we get an update event on the ydoc (from us)
        // then emit it to the rest of the clients
        ydoc.on('update', (update, origin) => {
            WebsocketService.getInstance().emit('y-update', update)
        })

        const update = () => {
            setText(ytext.toString())
        }

        ytext.observe(update)

        update()

        return () => ytext.unobserve(update)
    }, [])

    const handleChange = (e) => {
        const ytext = ytextRef.current

        if (ytext) {
            ytext.delete(0, ytext.length)
            ytext.insert(0, e.target.value)
        }
    }

 */

    useEffect(() => {
        WebsocketService.getInstance().connect({
            url: props.url,
            token: getToken(),
            handleConnect,
            handleDisconnect,
            handleError,
            handleEvent,
        })

        return () => {
            WebsocketService.getInstance().disconnect()
        }
    }, [])

    return (
        <WebsocketContext.Provider value={state}>
            {/* <textarea value={text} onChange={handleChange} /> */}
            {props.children}
        </WebsocketContext.Provider>
    )
}

export class WebsocketService {
    static instance
    socket
    listeners = {}

    constructor() {}

    static getInstance() {
        if (this.instance) return this.instance
        this.instance = new WebsocketService()
        return this.instance
    }

    emit(eventName: string, payload: any) {
        this.socket?.emit(eventName, payload)
    }

    disconnect() {
        this.socket.disconnect()
    }

    connect({ token, url, handleConnect, handleError, handleDisconnect, handleEvent }) {
        try {
            this.socket = io(url, {
                autoConnect: false,
                transports: ['websocket', 'polling'],
                auth: { token },
                query: { docName: 'shared-text' },
            })
            this.socket.connect()
            this.socket.on('connect', handleConnect)
            this.socket.on('connect_error', handleError)
            this.socket.on('disconnect', handleDisconnect)
            this.socket.onAny(handleEvent)
        } catch (e) {
            logger(e)
        }
    }
}
